import { useState, useEffect } from 'react';
import Alert from 'react-bootstrap/Alert';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { icon } from '@fortawesome/fontawesome-svg-core/import.macro';
import '../../assets/scss/components/common/stickyctapanel.scss';

/* props:
* 
* model
* handleSecondaryAction
* handlePrimaryAction
* primaryElementRef
* secondaryElementRef
* scrollingElementRef
* ctaType
* isLoading
*
* props.ctaType magic values:
* 1 - main content
* 2 - summary
* 
*/

export default function StickyCTAPanel(props)
{
    // show sticky cta panel if ATB or compat info panel not visible (short viewports)
    const [showStickyCTAPanel, setShowStickyCTAPanel] = useState(false);

    function handleResizeOrScroll() 
    {
        let element;

        if (props.secondaryElementRef?.current)
        {
            element = props.secondaryElementRef.current;
        }
        else if (props.primaryElementRef?.current)
        {
            element = props.primaryElementRef.current;
        }

        if (element) 
        {
            const elementRect = element.getBoundingClientRect();
            let isVisible;

            if (props.ctaType === 1)
            {
                isVisible = elementRect.top + 60 < window.innerHeight && elementRect.bottom >= 0;
            }
            else
            {
                isVisible = elementRect.top + 75 < window.innerHeight && elementRect.bottom >= 0;
            }

            if (!isVisible) 
            {
                // slide into page
                setShowStickyCTAPanel(true);
            }
            else 
            {
                // slide off page
                setShowStickyCTAPanel(false);
            }
        }
    }

    useEffect(() => 
    {
        // Call the function once to check visibility when the component mounts
        handleResizeOrScroll();

        // Add scroll and resize event listeners when the component mounts
        if (props.scrollingElementRef?.current)
        {
            props.scrollingElementRef.current.addEventListener('scroll', handleResizeOrScroll);
        }
        else
        {
            window.addEventListener('scroll', handleResizeOrScroll);
        }
        window.addEventListener('resize', handleResizeOrScroll);

        // Clean up the event listeners when the component unmounts
        return () =>
        {
            if (props.scrollingElementRef?.current)
            {
                props.scrollingElementRef.current.removeEventListener('scroll', handleResizeOrScroll);
            }
            else
            {
                window.removeEventListener('scroll', handleResizeOrScroll);
            }
            window.removeEventListener('resize', handleResizeOrScroll);
        };

    }); // No dependency array ensures the effect runs after every render

    const handleSecondaryActionClick = () =>
    {
        props.handleSecondaryAction();
    }

    const handlePrimaryActionClick = (e) =>
    {
        props.handlePrimaryAction(e);
    }

    return (
        <>
            {
                /* MainContent */
                (props.ctaType === 1)
                &&
                <div className={"sticky-cta-panel position-fixed w-100 bg-light p-3 d-none d-xl-block" + (showStickyCTAPanel ? " show" : "")}>
                    <div className="inner-container position-relative d-flex justify-content-between align-items-center mx-auto">
                        <div className="me-2">
                            <h3 className="mb-1 summary-build-price">&pound;{props.model.totalCostText} inc. VAT</h3>
                            {
                                props.model?.financeOptions && props.model?.monthlyPayment
                                &&
                                <>
                                    <label className="text-muted">from &pound;{props.model.monthlyPayment.toFixed(2)} per month.</label> <button className="paypal-modal-button text-muted border-0 bg-transparent text-decoration-underline pe-0 ps-2" data-bs-toggle="modal" data-bs-target="#financeModal">Find out more</button>
                                </>
                            }
                            {
                                props.model?.payPalCredit?.lowestMonthlyOption?.monthlyPayment
                                &&
                                <>
                                    <label className="text-muted">from &pound;{props.model.payPalCredit.lowestMonthlyOption.monthlyPayment.toFixed(2)} per month.</label> <button className="paypal-modal-button text-muted border-0 bg-transparent text-decoration-underline pe-0 ps-2" data-bs-toggle="modal" data-bs-target="#payPalCreditModal">Find out more</button>
                                </>
                            }
                            {
                                props.dopplePay
                                &&
                                <span className="finance-info-container text-start ms-sm-2 ms-lg-5">
                                    <button type="button" className="paypal-modal-button bg-transparent border-0 text-light ps-0 cursor-pointer" data-bs-toggle="modal" data-bs-target="#doppleModal"><label className="text-light d-inline cursor-pointer">{props.doppleText}</label> <span className="position-relative finance-message-icon"><FontAwesomeIcon icon={icon({ name: 'circle-info', style: 'solid' })} /></span></button>
                                </span>
                            }
                        </div>

                        <button className={"primary-button position-relative text-casing sticky-cta-panel-primary-button p-3 px-sm-5 mb-0 d-inline-block "} onClick={handlePrimaryActionClick} type="button">
                            {
                                props.ctaType === 1 ? "Continue" : "Add To Basket"
                            }
                            {
                                props.model.missingItems?.length > 0
                                &&
                                <span className="position-absolute primary-button-pill top-0 translate-middle badge rounded-pill bg-danger">
                                    {props.model.missingItems.length} missing item{props.model.missingItems.length > 1 ? <>s</> : null}
                                    <span className="visually-hidden">missing items</span>
                                </span>
                            }
                        </button>
                    </div>
                </div>
            }

            {
                /* Summary */
                (props.ctaType === 2)
                &&
                <div className={"sticky-cta-panel summary-bar position-fixed w-100 p-3 custom-bg-dark" + (showStickyCTAPanel ? " show" : "")}>
                    <div className="inner-container d-flex justify-content-between align-items-center mx-auto">
                        <div className="me-1 w-100">
                            <h3 className="mb-1 summary-build-price custom-color-light">&pound;{props.model.totalCostText} inc. VAT</h3>
                            {
                                props.model?.financeOptions && props.model?.monthlyPayment
                                &&
                                <>
                                    <label className="text-muted custom-color-light">from &pound;{props.model.monthlyPayment.toFixed(2)} per month.</label> <button className="paypal-modal-button text-muted border-0 bg-transparent text-decoration-underline pe-0 ps-2" data-bs-toggle="modal" data-bs-target="#financeModal">Find out more</button>
                                </>
                            }
                            {
                                props.model?.payPalCredit?.lowestMonthlyOption?.monthlyPayment
                                &&
                                <>
                                    <label className="text-muted custom-color-light">from &pound;{props.model.payPalCredit.lowestMonthlyOption.monthlyPayment.toFixed(2)} per month.</label> <button className="paypal-modal-button text-muted border-0 bg-transparent text-decoration-underline pe-0 ps-2" data-bs-toggle="modal" data-bs-target="#payPalCreditModal">Find out more</button>
                                </>
                            }
                            {
                                props.dopplePay
                                &&
                                <span className="finance-info-container text-start ms-sm-2 ms-lg-5">
                                    <button type="button" className="paypal-modal-button bg-transparent border-0 text-light ps-0 cursor-pointer" data-bs-toggle="modal" data-bs-target="#doppleModal"><label className="text-light d-inline cursor-pointer">{props.doppleText}</label> <span className="position-relative finance-message-icon"><FontAwesomeIcon icon={icon({ name: 'circle-info', style: 'solid' })} /></span></button>
                                </span>
                            }
                        </div>

                        {/*sticky-cta-red-underline-link red-underline-link */
                            (props.model?.compatibilityInfo?.length > 0)
                            ?
                                <Alert variant="danger" className="cursor-pointer ps-2 mb-0 ms-1" onClick={handleSecondaryActionClick}>
                                    <Alert.Heading className="fw-bold d-none d-md-inline">Incompatibility issue or incomplete build &gt;{'>'}</Alert.Heading>
                                    <Alert.Heading className="fw-bold d-inline d-md-none">Issues found &gt;{'>'}</Alert.Heading>
                                </Alert>
                            :
                                (props.isLoading === true)
                                ?
                                    <button className="primary-button sticky-cta-panel-primary-button spinner-button mb-0 d-inline-block w-100 ms-1" onClick={handlePrimaryActionClick} disabled={props.isLoading === true ? "disabled" : ""}>
                                        <div className="d-flex justify-content-center align-items-center" role="status">
                                            <div className="spinner-border" role="status">
                                                <span className="visually-hidden">Loading...</span>
                                            </div>
                                        </div>
                                    </button>
                                        
                                :
                                    <button className="primary-button sticky-cta-panel-primary-button mb-0 d-inline-block w-100 ms-1 py-3 custom-color-dark" onClick={handlePrimaryActionClick} disabled={props.isLoading === true ? "disabled" : ""}>
                                        { 
                                            props.ctaType === 1 ? ("CONTINUE") : ("ADD TO BASKET") 
                                        }
                                    </button>
                        }
                    </div>
                </div>
            }
        </>
    );
}